/**
 * Gom input desktop + mobile thành một trạng thái:
 * - WASD/mũi tên di chuyển, Shift chạy, E tương tác, Esc menu, M bản đồ, Q nhiệm vụ.
 * - Kéo chuột trái hoặc vuốt màn hình để xoay camera; giữ chuột phải = xoay tự do (khóa con trỏ, không trôi ra mép màn hình);
 *   Ctrl (nhấn rồi thả) hoặc C: ẩn/hiện chuột – khi ẩn, di chuột là xoay (không cần giữ nút); Esc cũng trả lại chuột.
 * - Lăn chuột / chụm 2 ngón để zoom.
 * - Joystick ảo ghi trực tiếp vào `stick` (UI ở apps/web).
 */
export class InputController {
  readonly keys = new Set<string>();
  stick = { x: 0, y: 0 };          // joystick ảo (-1..1)
  runToggle = false;                // nút chạy trên mobile
  lookDX = 0; lookDY = 0;           // tích lũy xoay camera giữa các frame
  zoomDelta = 0;
  enabled = true;
  private onAction: (a: string) => void;
  private pointers = new Map<number, { x: number; y: number }>();
  private pinchDist = 0;
  /** Độ nhạy chuột (0.3–3) và đảo trục dọc – chỉnh trong Cài đặt. */
  mouseSensitivity = 1;
  invertY = false;
  /** Chế độ chuột xoay camera (pointer lock): di chuột là xoay, không cần giữ nút. */
  mouseLook = false;
  private lastMouse: { x: number; y: number } | null = null;
  private holdLock = false; // khóa con trỏ tạm thời khi giữ chuột phải
  onMouseLookChange?: (on: boolean) => void;
  /** Kiểu minefun: nhấp chuột trái vào màn chơi là khóa chuột để xoay góc nhìn (Esc / Ctrl để thả). */
  clickToLook = true;

  constructor(private readonly surface: HTMLElement, onAction: (action: string) => void) {
    this.onAction = onAction;
    window.addEventListener('keydown', this.kd); window.addEventListener('keyup', this.ku); window.addEventListener('blur', this.clear);
    surface.addEventListener('pointerdown', this.pd); window.addEventListener('pointermove', this.pm); window.addEventListener('pointerup', this.pu); window.addEventListener('pointercancel', this.pu);
    surface.addEventListener('wheel', this.wh, { passive: false });
    surface.addEventListener('contextmenu', (e) => e.preventDefault());
    document.addEventListener('pointerlockchange', this.plc); window.addEventListener('mouseup', this.mu);
  }
  private mu = (e: MouseEvent) => { if (e.button === 2 && this.holdLock) { this.holdLock = false; if (!this.mouseLook) this.unlock(); } };

  get locked() { return document.pointerLockElement === this.surface; }
  private lock() { if (this.locked) return; try { const r = this.surface.requestPointerLock() as unknown as Promise<void> | undefined; r?.catch?.(() => undefined); } catch { /* trình duyệt không hỗ trợ */ } }
  private unlock() { if (this.locked) document.exitPointerLock(); }
  setMouseLook(on: boolean) {
    if (this.mouseLook === on) return;
    this.mouseLook = on; if (on) this.lock(); else this.unlock();
    this.onMouseLookChange?.(on);
  }
  private plc = () => {
    // trình duyệt tự thoát khóa (Esc, chuyển tab) ⇒ tắt chế độ chuột xoay để không bị kẹt
    if (!this.locked) { this.holdLock = false; if (this.mouseLook) { this.mouseLook = false; this.onMouseLookChange?.(false); } }
  };
  private addLook(dx: number, dy: number) { if (!this.enabled) return; this.lookDX += dx * this.mouseSensitivity; this.lookDY += dy * this.mouseSensitivity * (this.invertY ? -1 : 1); }

  private isTyping() { const el = document.activeElement; return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA'); }
  /** Ctrl nhấn rồi thả một mình (không kèm phím khác) ⇒ bật/tắt chế độ ẩn chuột */
  private ctrlAlone = false;
  private kd = (e: KeyboardEvent) => {
    if (this.isTyping()) { if (e.key === 'Escape') (document.activeElement as HTMLElement).blur(); return; }
    const k = e.key.toLowerCase();
    if (k === 'control') { if (!e.repeat) this.ctrlAlone = true; return; }
    this.ctrlAlone = false;
    if (e.ctrlKey || e.metaKey || e.altKey) return; // Ctrl+C, Ctrl+R… là phím của trình duyệt, không phải phím game
    if (!e.repeat) {
      if (k === 'c') { this.setMouseLook(!this.mouseLook); return; }
      if (k === 'f5' || k === 'p') { e.preventDefault(); this.onAction('view'); return; } // đổi góc nhìn thứ nhất / thứ ba
      // mở bảng/menu ⇒ trả con trỏ về để bấm được giao diện
      if (['e', 'escape', 'm', 'q', 't', 'i', 'enter', 'r', 'n', 'k', 'g', 'l', 'f', 'b', 'v', 'tab', 'j', 'h', 'u', 'y', 'x'].includes(k) && (this.mouseLook || this.holdLock)) { this.holdLock = false; this.setMouseLook(false); this.unlock(); }
      if (k === 'e') this.onAction('interact'); if (k === 'escape') this.onAction('menu'); if (k === 'm') this.onAction('map');
      if (k === 'q') this.onAction('quests'); if (k === 't') this.onAction('destiny'); if (k === 'i') this.onAction('inventory'); if (k === 'enter') { e.preventDefault(); this.onAction('chat'); } if (k === 'r') this.onAction('emote'); if (k === 'tab') { e.preventDefault(); this.onAction('openmenu'); } if (k === 'j') this.onAction('arena'); if (k === 'h') this.onAction('home');
      if (k === 'n') this.onAction('daily'); if (k === 'k') this.onAction('codex'); if (k === 'g') this.onAction('party'); if (k === 'l') this.onAction('lantern'); if (k === 'o') this.onAction('lamp'); if (k === 'u') this.onAction('guild'); if (k === 'y') this.onAction('market'); if (k === 'x') this.onAction('book'); if (k === 'z') this.onAction('mic');
      if (k === 'f') this.onAction('dance'); if (k === 'b') this.onAction('venues'); if (k === 'v') this.onAction('bigscreen');
    }
    if (this.enabled) this.keys.add(k);
  };
  private ku = (e: KeyboardEvent) => {
    const k = e.key.toLowerCase();
    if (k === 'control') { if (this.ctrlAlone && !this.isTyping()) this.setMouseLook(!this.mouseLook); this.ctrlAlone = false; return; }
    this.keys.delete(k);
  };
  private clear = () => { this.keys.clear(); this.pointers.clear(); };

  private pd = (e: PointerEvent) => {
    this.ctrlAlone = false; // Ctrl + nhấp chuột không tính là bấm Ctrl
    if (e.pointerType === 'mouse') {
      if (this.locked) return; // đang khóa: chuyển động lấy từ movementX/Y
      if (this.mouseLook) { this.lock(); return; } // nhấp lại vào màn chơi để khóa con trỏ
      if (e.button === 0 && this.clickToLook) this.setMouseLook(true); // không khóa được (trình duyệt chặn) thì vẫn kéo để xoay như thường
      if (e.button === 2) { this.holdLock = true; this.lock(); } // không khóa được thì vẫn kéo như thường
    }
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY }); if (this.pointers.size === 2) this.pinchDist = this.pinchSpan();
  };
  private pm = (e: PointerEvent) => {
    if (e.pointerType === 'mouse' && this.locked) {
      // movementX/Y là chuẩn khi khóa con trỏ; vài môi trường không cấp ⇒ dùng chênh lệch tọa độ
      let dx = e.movementX, dy = e.movementY;
      if (!dx && !dy && this.lastMouse) { dx = e.clientX - this.lastMouse.x; dy = e.clientY - this.lastMouse.y; }
      this.lastMouse = { x: e.clientX, y: e.clientY }; this.addLook(dx, dy); return;
    }
    if (e.pointerType === 'mouse') this.lastMouse = { x: e.clientX, y: e.clientY };
    const p = this.pointers.get(e.pointerId); if (!p) return;
    if (this.pointers.size === 2) { const d = this.pinchSpan(); this.zoomDelta += (this.pinchDist - d) * 0.02; this.pinchDist = d; }
    else if (e.pointerType === 'mouse') this.addLook(e.clientX - p.x, e.clientY - p.y);
    else if (this.enabled) { this.lookDX += e.clientX - p.x; this.lookDY += e.clientY - p.y; }
    p.x = e.clientX; p.y = e.clientY;
  };
  private pu = (e: PointerEvent) => {
    this.pointers.delete(e.pointerId);
    if (e.pointerType === 'mouse' && this.holdLock && e.button === 2) { this.holdLock = false; if (!this.mouseLook) this.unlock(); }
  };
  private wh = (e: WheelEvent) => { e.preventDefault(); this.zoomDelta += Math.sign(e.deltaY) * 0.9; };
  private pinchSpan() { const [a, b] = [...this.pointers.values()]; return Math.hypot(a.x - b.x, a.y - b.y); }

  /** Vector di chuyển theo khung camera: x = phải, y = tiến. */
  moveVector(): { x: number; y: number; run: boolean } {
    if (!this.enabled) return { x: 0, y: 0, run: false };
    let x = this.stick.x, y = this.stick.y;
    if (this.keys.has('w') || this.keys.has('arrowup')) y += 1; if (this.keys.has('s') || this.keys.has('arrowdown')) y -= 1;
    if (this.keys.has('d') || this.keys.has('arrowright')) x += 1; if (this.keys.has('a') || this.keys.has('arrowleft')) x -= 1;
    const len = Math.hypot(x, y); if (len > 1) { x /= len; y /= len; }
    const stickMag = Math.hypot(this.stick.x, this.stick.y);
    return { x, y, run: this.keys.has('shift') || this.runToggle || stickMag > 0.95 };
  }

  consumeLook() { const r = { dx: this.lookDX, dy: this.lookDY, zoom: this.zoomDelta }; this.lookDX = this.lookDY = this.zoomDelta = 0; return r; }

  dispose() {
    window.removeEventListener('keydown', this.kd); window.removeEventListener('keyup', this.ku); window.removeEventListener('blur', this.clear);
    window.removeEventListener('pointermove', this.pm); window.removeEventListener('pointerup', this.pu); window.removeEventListener('pointercancel', this.pu);
    this.surface.removeEventListener('pointerdown', this.pd); this.surface.removeEventListener('wheel', this.wh);
    document.removeEventListener('pointerlockchange', this.plc); window.removeEventListener('mouseup', this.mu); this.unlock();
  }
}
